/**
 * @file index.tsx
 *
 * @fileoverview Renders a ProductBox.
 */
import React from "react";
import FlexItems from "../../../02-atoms/06-layout/03-flex";
import FlexItem from "../../../02-atoms/06-layout/03-flex/02-flex-item";
import Spacing from "../../../02-atoms/06-layout/07-spacing";
import Text from "../../../02-atoms/13-text/03-text";
import styles from "./productBox.module.scss";
import { VariantProps, cva } from "class-variance-authority";

const textAlignmentStyle = cva([], {
  variants: {
    textAlignment: {
      left: styles.textAlignLeft,
      right: styles.textAlignRight,
      center: styles.textAlignCenter,
    },
  },
});

const buttonAlignmentStyle = cva([], {
  variants: {
    buttonAlignment: {
      left: styles.buttonAlignLeft,
      right: styles.buttonAlignRight,
      center: styles.buttonAlignCenter,
    },
  },
});

export interface ProductBoxProps
  extends VariantProps<typeof textAlignmentStyle>,
    VariantProps<typeof buttonAlignmentStyle> {
  /**
   * The banner for the product.
   */
  banner?: React.ReactNode;
  /**
   * The title of the product.
   */
  title: string;
  /**
   * The intro of the product.
   */
  intro: React.ReactNode;
  /**
   * The product price without currency.
   */
  price?: React.ReactNode;
  /**
   * The frequency of the product for subscription.
   */
  priceFrequency?: string;
  /**
   * Call to action button.
   */
  button?: React.ReactNode;
  /**
   * Footer text.
   */
  content?: React.ReactNode;
}

export const ProductBox = ({
  banner,
  title,
  intro,
  price,
  priceFrequency = " ",
  button,
  content,
  textAlignment,
  buttonAlignment,
}: ProductBoxProps) => {
  return (
    <div className={styles.productBox}>
      {banner}
      <div
        className={`${styles.productBoxHead} ${textAlignmentStyle({
          textAlignment,
        })}`}
      >
        <div>
          <Text fontWeight="bold" fontSize={4} marginTop={0}>
            {title}
          </Text>
          <Spacing size="medium" />
          <Text color="gray6" marginTop={0}>
            {intro}
          </Text>
          {content && (
            <div className={styles.productBoxContent}>
              <Spacing />
              {content}
            </div>
          )}
          <Spacing size="large" />
          {price && (
            <FlexItems gap={4} className={styles.price} alignItems="baseline">
              <FlexItem>
                <Text fontWeight="bold" fontSize={6} marginTop={0}>
                  {price}
                </Text>
              </FlexItem>
              <FlexItem>
                <Text color="gray6" fontSize={3} marginTop={0}>
                  {priceFrequency}
                </Text>
              </FlexItem>
            </FlexItems>
          )}
        </div>
        <Spacing size="medium" />
        {button && (
          <FlexItems className={buttonAlignmentStyle({ buttonAlignment })}>
            {button}
          </FlexItems>
        )}
      </div>
    </div>
  );
};
